import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { resetCookieConsent } from "./CookieBanner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Send,
  ArrowRight } from
"lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});
  const [companyInfo, setCompanyInfo] = useState({
    email: "info@lab336.ch",
    phone: "+41 79 664 08 15",
    street: "Dorf 1",
    zip_city: "9411 Reute AR",
    country: "Schweiz"
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    axios.get(`${API}/seo`).then(res => {
      const d = res.data || {};
      setSocialLinks({
        linkedin: d.social_linkedin || "",
        twitter: d.social_twitter || "",
        facebook: d.social_facebook || "",
        instagram: d.social_instagram || "",
      });
    }).catch(() => {});
    axios.get(`${API}/company-settings`).then(res => setCompanyInfo(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Nachricht erfolgreich gesendet! Wir melden uns bei Ihnen.");
      setFormData({ first_name: "", last_name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const navLinks = [
  { href: "#services", label: "Leistungen" },
  { href: "#software", label: "Portfolio" },
  { href: "#scheduler", label: "Terminvereinbarung" },
  { href: "#testimonials", label: "Referenzen" },
  { href: "#case-studies", label: "Fallstudien" },
  { href: "#blog", label: "Blog" }];


  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      id="contact"
      className="bg-lab-bg-dark grain-texture"
      data-testid="footer">

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Contact Info */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}>

            <span className="label-text">Kontakt</span>
            <h3 className="font-playfair text-3xl lg:text-4xl font-semibold text-white mt-4 mb-8">
              Lassen Sie uns über
              <br />
              Ihr Projekt sprechen
            </h3>

            <div className="space-y-6 mb-12">
              <a
                href={`mailto:${companyInfo?.email || "info@lab336.ch"}`}
                className="flex items-center gap-4 text-gray-300 hover:text-lab-accent transition-colors duration-200 group"
                data-testid="contact-email">

                <div className="w-12 h-12 bg-white/5 flex items-center justify-center group-hover:bg-lab-accent/20 transition-colors duration-200">
                  <Mail className="w-5 h-5" />
                </div>
                <span>{companyInfo?.email || "info@lab336.ch"}</span>
              </a>

              <a
                href={`tel:${(companyInfo?.phone || "+41796640815").replace(/\s/g, "")}`}
                className="flex items-center gap-4 text-gray-300 hover:text-lab-accent transition-colors duration-200 group"
                data-testid="contact-phone">

                <div className="w-12 h-12 bg-white/5 flex items-center justify-center group-hover:bg-lab-accent/20 transition-colors duration-200">
                  <Phone className="w-5 h-5" />
                </div>
                <span>{companyInfo?.phone || "+41 79 664 08 15"}</span>
              </a>

              <div className="flex items-center gap-4 text-gray-300">
                <div className="w-12 h-12 bg-white/5 flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <span>
                  {companyInfo?.street || "Dorf 1"}
                  <br />
                  {companyInfo?.zip_city || "9411 Reute AR"}{companyInfo?.country ? `, ${companyInfo.country}` : ", Schweiz"}
                </span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/5 flex items-center justify-center text-gray-300 hover:bg-lab-accent hover:text-lab-primary transition-colors duration-200"
                  data-testid="social-linkedin"
                  aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {socialLinks.twitter && (
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/5 flex items-center justify-center text-gray-300 hover:bg-lab-accent hover:text-lab-primary transition-colors duration-200"
                  data-testid="social-twitter"
                  aria-label="Twitter">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {socialLinks.facebook && (
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/5 flex items-center justify-center text-gray-300 hover:bg-lab-accent hover:text-lab-primary transition-colors duration-200"
                  data-testid="social-facebook"
                  aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {socialLinks.instagram && (
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/5 flex items-center justify-center text-gray-300 hover:bg-lab-accent hover:text-lab-primary transition-colors duration-200"
                  data-testid="social-instagram"
                  aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Vorname *</Label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="rounded-none bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-lab-accent"
                    placeholder="Max"
                    required
                    data-testid="contact-first-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Nachname *</Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="rounded-none bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-lab-accent"
                    placeholder="Mustermann"
                    required
                    data-testid="contact-last-name-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">E-Mail *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="rounded-none bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-lab-accent"
                    placeholder="max@beispiel.ch"
                    required
                    data-testid="contact-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Unternehmen</Label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="rounded-none bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-lab-accent"
                    placeholder="Firma AG"
                    data-testid="contact-company-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Nachricht *</Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="rounded-none bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-lab-accent min-h-[150px] resize-none"
                  placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
                  required
                  data-testid="contact-message-input" />

              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-secondary rounded-none w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit-btn">

                {isSubmitting ?
                "Wird gesendet..." :

                <>
                    <Send className="w-4 h-4" />
                    Nachricht senden
                  </>
                }
              </button>
            </form>
          </motion.div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-8 items-center">
            {/* Logo */}
            <div>
              <a
                href="#"
                className="font-playfair text-4xl lg:text-5xl font-semibold text-white tracking-tight"
                data-testid="footer-logo">LAB336


              </a>
              <p className="text-gray-400 mt-4 max-w-xs">
                Beratung für Prozessoptimierung, Digitalisierung und KI-Integration.
              </p>
            </div>

            {/* Navigation */}
            <div className="lg:justify-self-center">
              <nav className="flex flex-wrap gap-x-8 gap-y-3">
                {navLinks.map((link) =>
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-gray-400 hover:text-lab-accent text-sm transition-colors duration-200">

                    {link.label}
                  </button>
                )}
              </nav>
            </div>

            {/* Copyright */}
            <div className="lg:justify-self-end">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} LAB 336. Alle Rechte vorbehalten.
              </p>
              <div className="flex gap-4 mt-2">
                <Link
                  to="/datenschutz"
                  className="text-gray-500 hover:text-gray-300 text-sm"
                  data-testid="footer-datenschutz-link"
                >
                  Datenschutz
                </Link>
                <Link
                  to="/impressum"
                  className="text-gray-500 hover:text-gray-300 text-sm"
                  data-testid="footer-impressum-link"
                >
                  Impressum
                </Link>
                <Link
                  to="/ueber-uns"
                  className="text-gray-500 hover:text-gray-300 text-sm"
                  data-testid="footer-ueber-uns-link"
                >
                  Über uns
                </Link>
                <button
                  onClick={resetCookieConsent}
                  className="text-gray-500 hover:text-gray-300 text-sm"
                  data-testid="footer-cookie-link"
                >
                  Cookie-Einstellungen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;